import { Button } from '@/components/Common';
import { Modal } from '@/components/Layout';
import bannerYellow from '@/assets/icons/banneryellow.svg';
import moneyYellow from '@/assets/icons/moneyYellow.svg';
import iconDateYellow from '@/assets/icons/iconDateYellow.svg';
import bxs_school from '@/assets/icons/bxs_school.svg';
import iconUpdate from '@/assets/icons/iconUpdate.svg';
import positionIcon from '@/assets/icons/positionYellow.svg';
import timeIcon from '@/assets/icons/time.svg';
import { Campaign } from '../Campaign.types';
import {
  CampaignType,
  getCampaignType,
  getStatusDisplay,
} from '../useCampaign';
import { CampaignStatOutput, CampaignStatus } from '@/api/gql/graphql';

import { useCampaignDetail } from './campaignDetail.hook';
import StatisticCard from './StatisticCard';
import AudioPlayer from './AudioPlayer';
import StatisticsChart from './StatisticsChart';
import ChartSection from './ChartSection';
import { memo } from 'react';

const detailItemStyle = 'flex justify-start gap-3 pb-[17px]';

const filterOptions = [
  { name: 'Vues', _id: 'views' },
  { name: 'Clics', _id: 'clicks' },
];

const colors = ['#FEC200', '#FF5500', '#FFFFFF'];

const formatGender = (gender: string): string => {
  return gender === 'M' ? 'Homme' : gender === 'F' ? 'Femme' : gender;
};
const StatisticsSection = memo(
  ({
    campaignStats,
    getConversionRate,
  }: {
    campaignStats: CampaignStatOutput;
    getConversionRate: (views: number, clicks: number) => number;
  }) => (
    <div className="flex gap-4">
      <div className="flex gap-4">
      <StatisticsChart
        title="Répartition"
        subtitle="par genre"
        data={campaignStats?.byGender || []}
        colors={colors}
        filterOptions={filterOptions}
        labelKey="gender"
        formatLabel={formatGender}
      />
       <StatisticsChart
        title="Répartition"
        subtitle="par classe"
        data={campaignStats?.byClass || []}
        colors={colors}
        filterOptions={filterOptions}
        labelKey="class"
      />
      </div>
    </div>
  ),
);

StatisticsSection.displayName = 'StatisticsSection';

const StatisticCards = memo(
  ({
    campaign,
    getConversionRate,
  }: {
    campaign: Campaign;
    getConversionRate: (views: number, clicks: number) => number;
  }) => (
    <div className="w-full">
      <StatisticCard
        title="Nombre de Clics"
        value={campaign.campaignClicks}
        icon={bxs_school}
      />
      <StatisticCard
        title="Nombre de vues"
        value={campaign.campaignViews}
        icon={bxs_school}
      />
      <StatisticCard
        title="Taux de conversion"
        value={getConversionRate(
          campaign.campaignViews,
          campaign.campaignClicks,
        )}
        icon={bxs_school}
      />
    </div>
  ),
);

StatisticCards.displayName = 'StatisticCards';

const CampaignDetail: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
  const {
    isOpenCampaignDetailModal,
    openCampaignDetailModal,
    audioRef,
    isPlaying,
    togglePlay,
    formatDate,
    getDurationInDays,
    downloadFile,
    getConversionRate,
    handleUpdateStatus,
    downloading,
    getButtonTitle,
    campaignStats,
    campaignDailyStats,
  } = useCampaignDetail(campaign);
  
  return (
    <Modal
      isOpen={isOpenCampaignDetailModal}
      onClose={openCampaignDetailModal}
      headerStyle="m-0 p-0 bg-bgModal"
      modalStyle="bg-bgModal"
    >
      <div className="text-white grid grid-cols-[368.12px_1fr] rounded-lg max-w-7xl mx-auto">
        <div className="mr-11">
          <div className="relative w-80 h-80 mb-5 rounded-lg flex items-center justify-center bg-bg">
            {campaign.campaignType === CampaignType.AUDIO_PRE_LECON ? (
              <AudioPlayer
                audioRef={audioRef}
                isPlaying={isPlaying}
                togglePlay={togglePlay}
                campaignResource={campaign.campaignResource}
              />
            ) : (
              <img
                src={campaign.campaignResource}
                alt="Campaign"
                className="w-full h-full object-contain rounded-lg"
              />
            )}
            <Button
              className="absolute top-[5.58px] right-[5.58px] !bg-black/45 text-[13.39px] text-white px-3 py-1 text-sm rounded"
              title="Modifier"
              icon={<img src={iconUpdate} alt="iconUpdate" />}
              iconPosition="left"
            />
          </div>
          <div>
            <h2 className="text-[22.32px] font-bold">
              Campagne {campaign.company.name}
            </h2>
            <span className="text-yellow-500">
              {getStatusDisplay(campaign.campaignStatus.toLocaleUpperCase())}
            </span>
            <ul className="mt-8 text-[13.39px] w-72 text-white font-normal space-y-1 ">
              <li className={detailItemStyle}>
                <img src={bannerYellow} alt="bannerYellow" />
                <span>
                  {getCampaignType(campaign.campaignType as CampaignType)}
                </span>
              </li>
              <li className={detailItemStyle}>
                <img src={iconDateYellow} alt="iconDateYellow" />
                <span>
                  Début : <span>{formatDate(campaign.campaignStartDate)}</span>
                </span>
              </li>
              <li className={detailItemStyle}>
                <img src={positionIcon} alt="position" />
                <span>
                  {campaign.campaignType === CampaignType.MAIN_BANNER
                    ? 'Position'
                    : 'Priorité'}{' '}
                  : <span>{campaign.campaignPriority}</span>
                </span>
              </li>
              <li className={detailItemStyle}>
                <img src={timeIcon} alt="time" />
                <span>
                  {getDurationInDays(
                    campaign.campaignStartDate,
                    campaign.campaignEndDate,
                  )}{' '}
                  jours dont{' '}
                  <span>
                    {getDurationInDays(campaign.campaignEndDate, new Date())}{' '}
                    jours restants
                  </span>
                </span>
              </li>
              <li className={detailItemStyle}>
                <img src={moneyYellow} alt="money" />
                <span>
                  Budget : <span>{campaign.campaignAmount} CFA</span>
                </span>
              </li>
            </ul>
            {campaign.campaignStatus.toLocaleUpperCase() ===
            CampaignStatus.Running ? (
              <Button
                className="w-80 mt-8 !bg-[#BD292F] text-white"
                title="Stopper"
                onClick={() => handleUpdateStatus(CampaignStatus.Completed)}
              />
            ) : (
              <Button
                className="w-80 mt-8"
                title={getButtonTitle(campaign.campaignStatus)}
                onClick={() => handleUpdateStatus(CampaignStatus.Running)}
              />
            )}

            {(campaign.campaignStatus.toLocaleUpperCase() ===
              CampaignStatus.Running ||
              campaign.campaignStatus.toLocaleUpperCase() ===
                CampaignStatus.Completed) && (
              <Button
                className="w-80 mt-8 !bg-[#131311] text-primary"
                title={
                  downloading ? 'Téléchargement...' : 'Télécharger les stats'
                }
                onClick={downloadFile}
              />
            )}
          </div>
        </div>

        <div className="w-full mb-6 bg-[#2D2D2D] p-4 rounded-[5.58px] h-[773.58px] overflow-y-auto overflow-x-hidden">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[252px_523px]">
            <StatisticCards
              campaign={campaign}
              getConversionRate={getConversionRate}
            />
            <StatisticsSection
              campaignStats={
                campaignStats || { _id: '', byClass: [], byGender: [] }
              }
              getConversionRate={getConversionRate}
            />
          </div>
          <div className="mt-[13px]">
            <ChartSection
              campaignDailyStats={campaignDailyStats || []}
              campaignStats={
                campaignStats || {
                  _id: '',
                  byRegion: [],
                  byClass: [],
                  byGender: [],
                }
              }
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CampaignDetail;
